import { PatientInfoDrawer, DisclaimerModal } from '@/entities/study';
import {
  DetailTitleComponent,
  AlertBanner,
  CustomTabs as CustomTabsComponent,
} from '@/shared/ui/components';
import { InboxECGViewer } from '@/widgets/ecg-viewer';
import { PendingCommandIcon } from '@/shared/ui/assets';
import { auth } from '@/shared/lib';
import { checkHideInstantReportTab } from '@/entities/facility';
import classNames from 'classnames';
import _ from 'lodash';
import { t } from 'ttag';
import EcgViewerTab from './ecgViewerTab/ecgViewerTab';
import EventECGViewer from './eventsTab/eventECGViewer';
import EventsTab from './eventsTab/eventsTab';
import PatientDetailsTab from './patientDetailsTab/patientDetailsTab';
import ReportsTab from './reportsTab/reportsTab';
import ReturnStatusTab from './returnStatusTab/returnStatusTab';
import StudyDetailsTab from './studyDetailsTab/studyDetailsTab';
import BiodirectStatusTab from './biodirectStatusTab/biodirectStatusTab';
import CommentBox from './commentBox/commentBox';
import InstantReportTab from './instantReportTab/instantReportTab';
import { generateTitleBlockArray } from '../lib/studyManagement';
import { useStudyManagement } from './useStudyManagement';
import './studyManagement.scss';

const CustomTabs = CustomTabsComponent as any;

/**
 * Renders the study management detail page: disclaimer modal, patient info drawer, study title header with its comment box toggle, and the vertical tab set from the tab descriptor builder. Inbox and event ECG viewers replace the tab content when their data exists, an info banner marks aborted studies as non-billable, a fixed banner warns that aborting or stopping needs the device online, and root class names track the aborted, aborting and archived states.
 *
 * @returns The full study management page.
 */
const StudyManagementPage = () => {
  const {
    studyId,
    state,
    commentBoxVisible,
    setCommentBoxVisible,
    headerRef,
    isWebView,
    isAbortingStudy,
    isAbortedStudy,
    isArchivedStudy,
    isC1cProviderStudy,
    hasStoppingStudyCommand,
    onClickBack,
    navigateTab,
    toggleDisplayPatientInfoDrawer,
    toggleECGViewer,
    toggleInboxECGViewer,
    updateStudyDetail,
    updateStudyData,
    handleUpdateReports,
    onClickOkayDisclaimerModal,
    updateReturnStatus,
    updateSentReferenceCodeStatus,
    handleAbortStudy,
    handleViewVerifiedReportClick,
    resetShouldLoadVerifiedReport,
    handleUpdateStudyAfterAssigningDevice,
    handleUpdateReferringPhysicians,
    handleRefreshTracking,
  } = useStudyManagement();

  /**
   * Builds the tab descriptors rendered by the study management page. Study details and patient details are always present, reports, events and ECG viewer are appended only when the study is neither aborted nor aborting, then instant reports (unless hidden), return status and Biodirect status tabs are pushed conditionally, with every tab disabled until the study data has loaded.
   *
   * @returns Ordered tab descriptors with label, key, rendered children and disabled state.
   */
  const listTab = () => {
    let arrayTab = [
      {
        label: (
          <div className="tab-container">
            <span className="tab-text">{t`Study details`}</span>
          </div>
        ),
        key: '1',
        children: (
          <StudyDetailsTab
            isActive
            isLoadedData={state.isLoadedData}
            data={state.studyDetails}
            updateStudyDetail={updateStudyDetail}
            updateSentReferenceCodeStatus={updateSentReferenceCodeStatus}
            handleAbortStudy={handleAbortStudy}
            referringPhysicians={state.referringPhysicians}
            interprettingPhysicians={state.interprettingPhysicians}
            isAbortedStudy={isAbortedStudy}
            isAbortingStudy={isAbortingStudy}
            hasLoginBanner={state.hasLoginBanner}
            handleUpdateStudyAfterAssigningDevice={handleUpdateStudyAfterAssigningDevice}
            countries={state.countries}
            handleUpdateReferringPhysicians={handleUpdateReferringPhysicians}
            commentBoxVisible={commentBoxVisible}
            updateStudyData={updateStudyData}
            studyLogs={state.studyLogs}
            isC1cProviderStudy={isC1cProviderStudy}
          />
        ),
        disabled: !state.isLoadedData,
      },
      {
        label: (
          <div className="tab-container">
            <span className="tab-text">{t`Patient details`}</span>
          </div>
        ),
        key: '2',
        children: !isWebView.current && (
          <PatientDetailsTab
            isActive
            isLoadedData={state.isLoadedData}
            data={state.studyDetails}
            countries={state.countries}
            updateStudyDetail={updateStudyDetail}
            isAbortedStudy={isAbortedStudy}
            isAbortingStudy={isAbortingStudy}
            isC1cProviderStudy={isC1cProviderStudy}
          />
        ),
        disabled: !state.isLoadedData,
      },
    ];
    if (!isAbortedStudy && !isAbortingStudy) {
      arrayTab = arrayTab.concat([
        {
          label: (
            <div className="tab-container">
              <span className="tab-text">{t`Reports`}</span>
            </div>
          ),
          key: '3',
          children: (
            <ReportsTab
              isLoadedData={state.isLoadedData}
              data={state.studyDetails}
              toggleInboxECGViewer={toggleInboxECGViewer}
              updateData={handleUpdateReports}
              hasLoginBanner={state.hasLoginBanner}
              shouldLoadVerifiedReport={state.shouldLoadVerifiedReport}
              resetShouldLoadVerifiedReport={resetShouldLoadVerifiedReport}
            />
          ),
          disabled: !state.isLoadedData,
        },
        {
          label: (
            <div className="tab-container">
              <span className="tab-text">{t`Events`}</span>
            </div>
          ),
          key: '4',
          children: (
            <EventsTab
              isActive
              data={state.studyDetails}
              toggleECGViewer={toggleECGViewer}
              hasLoginBanner={state.hasLoginBanner}
            />
          ),
          disabled: !state.isLoadedData,
        },
        {
          label: (
            <div className="tab-container">
              <span className="tab-text">{t`ECG viewer`}</span>
            </div>
          ),
          key: '5',
          children: (
            <EcgViewerTab
              isActive
              data={state.studyDetails}
              hasLoginBanner={state.hasLoginBanner}
            />
          ),
          disabled: !state.isLoadedData,
        },
      ]);
    }

    const shouldHideInstantReportTab = checkHideInstantReportTab();
    if (!shouldHideInstantReportTab) {
      arrayTab.push({
        label: (
          <div className="tab-container">
            <span className="tab-text">{t`Instant reports`}</span>
          </div>
        ),
        key: '6',
        children: (
          <InstantReportTab
            isActive={state.activeTab === '6'}
            isLoadedData={state.isLoadedData}
            data={state.studyDetails}
            hasLoginBanner={state.hasLoginBanner}
            handleViewVerifiedReportClick={handleViewVerifiedReportClick}
            isC1cProviderStudy={isC1cProviderStudy}
          />
        ),
        disabled: !state.isLoadedData,
      });
    }

    if (state.hasReturnStatus) {
      arrayTab.push({
        label: (
          <div className="tab-container">
            <span className="tab-text">{t`Return status`}</span>
          </div>
        ),
        key: '7',
        children: (
          <ReturnStatusTab
            isLoadedData={state.isLoadedData}
            data={state.studyDetails}
            updateStudyDetail={updateReturnStatus}
            countries={state.countries}
            hasLoginBanner={state.hasLoginBanner}
            handleRefreshTracking={handleRefreshTracking}
            isC1cProviderStudy={isC1cProviderStudy}
          />
        ),
        disabled: !state.isLoadedData,
      });
    }

    if (state.studyDetails.isBiofluxDirect) {
      arrayTab.push({
        label: (
          <div className="tab-container">
            <span className="tab-text">{t`Biodirect status`}</span>
          </div>
        ),
        key: '8',
        children: (
          <BiodirectStatusTab
            isLoadedData={state.isLoadedData}
            study={state.studyDetails}
            hasLoginBanner={state.hasLoginBanner}
            handleRefreshTracking={handleRefreshTracking as any}
          />
        ),
        disabled: !state.isLoadedData,
      });
    }

    return arrayTab;
  };

  return (
    <>
      <DisclaimerModal
        isOpen={state.isOpenDisclaimerModal}
        rightButtonName={t`Okay`}
        onClickRightButton={onClickOkayDisclaimerModal}
        study={state.studyDetails}
      />

      <PatientInfoDrawer
        visible={state.isVisiblePatientInfoDrawer}
        studyId={studyId}
        handleClickCloseButton={toggleDisplayPatientInfoDrawer}
      />

      <div
        id={isWebView.current ? 'studies-detail-web-view' : undefined}
        className={classNames(
          'custom-page clinic-electrophysiologist study-management-page animated fadeIn',
          {
            relative: !_.isEmpty(state.ecgViewerData),
            '--aborted': isAbortedStudy,
            '--aborting': isAbortingStudy,
            '--is-archived': isArchivedStudy,
            'has-marketing-banner': auth.hasMarketingBanner() || auth.hasLogoBanner(),
            'has-login-banner': state.hasLoginBanner,
          },
        )}
      >
        {!_.isEmpty(state.inboxECGViewerData) && (
          <InboxECGViewer data={state.inboxECGViewerData} handleClickBack={toggleInboxECGViewer} />
        )}
        {!_.isEmpty(state.ecgViewerData) && (
          <EventECGViewer
            data={state.ecgViewerData}
            handleClickBack={toggleECGViewer as unknown as () => void}
          />
        )}

        <div className="detail-study-container">
          <div
            className={classNames(
              commentBoxVisible ? 'content-container with-comment-box' : 'content-container',
            )}
          >
            <div
              ref={headerRef}
              className={classNames('top-container', {
                '--has-box-shadow': state.isScroll && state.activeTab !== '4',
              })}
            >
              <DetailTitleComponent
                isLoadedData={state.isLoadedData}
                title={t`Study`}
                handleClickBack={onClickBack}
                hasCommentBox
                commentBoxVisible={commentBoxVisible}
                onToggle={() => {
                  setCommentBoxVisible(!commentBoxVisible);
                }}
                titleBlockArray={generateTitleBlockArray({
                  studyDetails: state.studyDetails,
                  toggleDisplayPatientInfoDrawer,
                })}
              />
            </div>
            {isAbortedStudy && (
              <div className="aborted-info-label">
                <AlertBanner
                  alertType="info"
                  description={t`This study has been aborted and will not be counted toward your billing.`}
                />
              </div>
            )}
            {(isAbortingStudy || hasStoppingStudyCommand) && (
              <div className="aborting-study__fixed-container">
                <div className="aborting-study__container">
                  <img
                    className="aborting-study__icon"
                    src={PendingCommandIcon}
                    alt="Pending command icon"
                  />
                  <span className="aborting-study__message">
                    {isAbortingStudy
                      ? t`Aborting study. Please ensure your device is online to complete the process.`
                      : t`Stopping study. Please ensure your device is online to complete the process.`}
                  </span>
                </div>
              </div>
            )}
            <CustomTabs
              mode="vertical"
              className={classNames('custom-style-tab study-management-custom-tab', {
                '--hide': !_.isEmpty(state.inboxECGViewerData) || !_.isEmpty(state.ecgViewerData),
              })}
              activeKey={state.activeTab}
              onChange={(key: any) => {
                navigateTab(key);
              }}
              items={listTab() as any}
            />
          </div>
          <div className="comment-container">
            <CommentBox
              isVisible={commentBoxVisible}
              studyId={studyId ?? ''}
              ticketType="Study"
              isLoadedData={state.isLoadedData}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default StudyManagementPage;
